import React, { useRef } from 'react';
import { Upload as UploadIcon, X, CheckCircle, AlertCircle, FileSpreadsheet, Eye } from 'lucide-react';
import type { FileUpload as FileUploadType } from '../types';
import { theme } from '../styles/theme';

interface UploadTabProps {
  files: File[];
  setFiles: (files: File[]) => void;
  uploading: boolean;
  uploadProgress: { [key: string]: number };
  error: string;
  setError: (error: string) => void;
  uploadedFiles: FileUploadType[];
  dragActive: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onUploadMore: () => void;
  onNextStep: () => void;
  onPreview: (file: FileUploadType) => void;
  formatFileSize: (bytes: number) => string;
}

const UploadTab: React.FC<UploadTabProps> = ({
  files,
  uploading,
  uploadProgress,
  error,
  uploadedFiles,
  dragActive,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileSelect,
  onRemoveFile,
  onUploadMore,
  onNextStep,
  onPreview,
  formatFileSize,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {error && (
        <div className={`mb-6 ${theme.status.error.background} border ${theme.status.error.border} ${theme.status.error.text} px-4 py-3 rounded-lg flex items-start`}>
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      {/* Only show upload area if no file is uploaded */}
      {uploadedFiles.length === 0 && (
        <div className={`${theme.card.background} rounded-lg shadow-lg p-8 mb-6`}>
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 ${
              dragActive
                ? 'border-interactive-primary bg-interactive-primary/10'
                : 'border-border hover:border-interactive-primary'
            }`}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            {files.length === 0 ? (
              <>
                <div className={`${theme.status.info.background} p-4 rounded-full mb-4`}>
                  <UploadIcon className="w-12 h-12 mx-auto text-interactive-primary" />
                </div>
                <h3 className={`text-xl font-semibold ${theme.text.primary} mb-2`}>
                  Drop your file here
                </h3>
                <p className={`${theme.text.secondary} mb-4`}>
                  or click to browse
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={onFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => inputRef.current?.click()}
                  className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg transition duration-200"
                >
                  Select File
                </button>
                <p className="text-sm text-text-tertiary mt-4">
                  Supported formats: CSV, Excel (.xlsx, .xls) • One file at a time
                </p>
                <p className="text-xs text-text-tertiary mt-2">
                  📏 Limits: Max 25MB file size • Max 500,000 rows
                </p>
              </>
            ) : null}
          </div>

          {/* Selected Files for Upload */}
          {files.length > 0 && (
            <div className="space-y-3 mt-6">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between p-4 bg-bg-subtle rounded-lg"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <div className={`${theme.status.info.background} p-2 rounded flex-shrink-0`}>
                      <FileSpreadsheet className="w-5 h-5 text-interactive-primary" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className={`text-sm font-medium ${theme.text.primary} truncate`}>
                        {file.name}
                      </p>
                      <p className="text-sm text-text-tertiary">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  {uploadProgress[file.name] !== undefined ? (
                    <div className="flex items-center ml-4">
                      <div className="w-32 bg-bg-hover rounded-full h-2 mr-3">
                        <div
                          className="bg-interactive-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[file.name]}%` }}
                        />
                      </div>
                      <span className="text-sm text-text-secondary w-12">
                        {uploadProgress[file.name]}%
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => onRemoveFile(index)}
                      className="ml-4 p-2 text-qc-error hover:bg-qc-error/20 rounded-lg transition-colors"
                      disabled={uploading}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Uploaded Files Summary */}
      {uploadedFiles.length > 0 && (
        <div className={`${theme.card.background} rounded-lg shadow-lg p-6`}>
            <div className="space-y-3">
              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className={`${theme.card.background} p-4 rounded-lg border border-qc-success/30`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <CheckCircle className="w-5 h-5 text-qc-success mr-3" />
                      <div>
                        <p className={`text-sm font-medium ${theme.text.primary}`}>
                          {file.original_filename}
                        </p>
                        <p className={`text-xs ${theme.text.secondary}`}>
                          {file.row_count?.toLocaleString()} rows × {file.column_count} columns
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onPreview(file)}
                        className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg flex items-center text-sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </button>
                      <button
                        onClick={onUploadMore}
                        className="px-4 py-2 border border-qc-error text-qc-error hover:bg-qc-error/20 rounded-lg flex items-center text-sm transition-colors"
                        title="Remove file and upload a different one"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex justify-end mt-4">
                <button
                  onClick={onNextStep}
                  className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg transition duration-200"
                >
                  Next Step: Configure Prompt →
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default UploadTab;
